import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { auth } from "../../firebase";
import UploadZipFile from "../documentation/UploadZipFile";
import ConfigureWorkspaceSpaceImport from "./ConfigureWorkspaceSpaceImport";
import ProcessWorkspaceSpaceImport from "./ProcessWorkspaceSpaceImport";
import "../../css/ImportDocument.css";
import "../../css/DocumentationHomeComponent.css";
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";
import "../../css/HomePage.css"

function ImportWorkspaceSpace({ onComplete, onBack }) {
    const navigate = useNavigate();
    const { customDomain } = useParams();
    const [currentStep, setCurrentStep] = useState('intro'); // 'intro', 'upload', 'configure', 'process', 'completing'
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [completionData, setCompletionData] = useState(null);
    const [uid, setUid] = useState("");
    const [workspaceDetails, setWorkspaceDetails] = useState(null);
    const { enhancedRightClickEnabled } = useQuickLinks();
    
    // ZIP file and extracted data
    const [extractedData, setExtractedData] = useState(null);
    
    // Configuration data
    const [spaceConfig, setSpaceConfig] = useState(null);

  const loggedOutOptions = [
    {
      label: "Login",
      icon: <i className="fas fa-sign-in-alt"></i>,
      url: "/login",
      onClick: () => navigate("/login"),
    },
    {
      label: "Register",
      icon: <i className="fas fa-user-plus"></i>,
      url: "/register",
      onClick: () => navigate("/register"),
    },
    {
      label: "Try My Notebook",
      icon: <i className="fas fa-book"></i>,
      url: "/my-notebook",
      onClick: () => navigate("/my-notebook"),
    },
    {
      label: "Visit YouTube Channel",
      icon: <i className="fab fa-youtube"></i>,
      url: "https://www.youtube.com/@schoolofintology4062",
      onClick: () => window.open("https://www.youtube.com/@schoolofintology4062", "_blank", "noopener,noreferrer"),
    },
    {
      label: "Join Discord",
      icon: <i className="fab fa-discord"></i>,
      url: "https://discord.gg/DYkESHwgVA",
      onClick: () => window.open("https://discord.gg/DYkESHwgVA", "_blank", "noopener,noreferrer"),
    },
  ];

const loggedInOptions = [
      {
        label: "Home",
        icon: <i className="fas fa-home"></i>,
        url: "/",
        onClick: () => navigate("/"),
      },
  {
    label: "Your Documents",
    url: "/documentation",
    onClick: () => navigate("/documentation"),
  },
  {
    label: "Your Walkthroughs",
    url: "/walkthroughs",
    onClick: () => navigate("/walkthroughs"),
  },
  {
    label: "Your Notebook",
    url: "/my-notebook",
    onClick: () => navigate("/my-notebook"),
  },
  {
    label: "Your Workspace",
    url: "/workspaces",
    onClick: () => navigate("/workspaces"),
  },
  {
    label: "Account Settings",
    url: `/account/${uid}`,
    onClick: () => navigate(`/account/${uid}`),
  },
];

  // Use loggedInOptions if signed in, otherwise loggedOutOptions
  const menuOptions = uid ? loggedInOptions : loggedOutOptions;
  const { showContextMenu, ContextMenuComponent } = useContextMenu(menuOptions);

    // Authentication Effect
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUid(user.uid);
            } else {
                navigate("/login");
            }
        });

        return () => {
            unsubscribe();
        };
    }, [navigate]);

    // Fetch workspace details
    useEffect(() => {
        if (uid && customDomain) {
            fetchWorkspaceDetails();
        }
    }, [uid, customDomain]);

    const fetchWorkspaceDetails = async () => {
        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) {
                throw new Error("User is not authenticated.");
            }

            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/domain/${customDomain}/`,
                {
                    headers: {
                        "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
                        "Requesting-User-Uid": uid,
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                setWorkspaceDetails(data);
            } else {
                console.error("Failed to fetch workspace details");
                navigate("/workspaces");
            }
        } catch (error) {
            console.error("Error fetching workspace details:", error);
            navigate("/workspaces");
        }
    };

    const handleArrowClick = () => {
        setIsTransitioning(true);
        // Small delay to allow transition to start
        setTimeout(() => {
            setCurrentStep('upload');
            setIsTransitioning(false);
        }, 150);
    };

    const handleZipUploaded = (data) => {
        setExtractedData(data);
        setCurrentStep('configure');
    };

    const handleConfigurationComplete = (config) => {
        setSpaceConfig(config);
        
        // Check if the import was already completed in ConfigureWorkspaceSpaceImport
        if (config.importResult) {
            // Import is already complete, go directly to completion
            handleProcessComplete(null, config.importResult.pages, config.spaceName, config.authorUid);
        } else {
            // Import not yet complete, go to processing step
            setCurrentStep('process');
        }
    };

    const handleBackToIntro = () => {
        setCurrentStep('intro');
        setExtractedData(null);
        setSpaceConfig(null);
        setIsTransitioning(false);
    };

    const handleBackToUpload = () => {
        setCurrentStep('upload');
        setSpaceConfig(null);
    };

    const handleBackToConfigure = () => {
        setCurrentStep('configure');
    };

    const handleBackClick = () => {
        if (onBack) {
            setIsTransitioning(true);
            setTimeout(() => {
                onBack();
            }, 150);
        } else {
            // Fallback to navigate to workspace if no onBack prop
            navigate(`/workspace/${customDomain}`);
        }
    };

    const handleProcessComplete = (spaceId, pages, spaceName = null, authorUid = null) => {
        setCompletionData({ spaceId, pages });
        setCurrentStep('completing');
        
        // Use the provided spaceName or fall back to spaceConfig.spaceName
        const nameToUse = spaceName || spaceConfig?.spaceName;
        
        // Show success message briefly, then redirect to the workspace
        setTimeout(() => {
            navigate(`/workspace/${customDomain}`);
        }, 2000); // 2 second delay to show completion message
    };

    const renderStep = () => {
        switch (currentStep) {
            case 'intro':
                return (
                    <>
                        <div className="ImportDocument__content">
                            <h2>Import Confluence Space to Workspace</h2>

                            <p className="medium" style={{ marginTop: "20px" }}>
                                Thank you for choosing Bluebird Documentation! This process allows you to import 
                                an entire Confluence space with all its pages and hierarchy directly into your team workspace.
                            </p>

                            <p className="small">
                                We'll try to make instructions as clear as possible. If you're facing issues, need further guidance, 
                                or have encountered a bug, please feel free to submit a support ticket.
                            </p>

                            <p className="medium semi-bold">
                                Below is a general overview of the process.
                            </p>

                            <ol>
                                <li>Click the arrow on the right of the page to begin.</li>
                                <li>Upload the ZIP file you exported from Confluence.</li>
                                <li>Configure your space import settings (name, which pages to include, etc.).</li>
                                <li>We'll process the import and create your space with all pages and hierarchy in your workspace.</li>
                                <li>Review and access your newly imported space in your workspace!</li>
                            </ol>

                            <p className="small">
                                This feature imports entire Confluence spaces while preserving page relationships and structure directly into your team workspace. 
                                We really appreciate you trying this feature, and please feel free to submit feedback so we can continue to improve it!
                            </p>
                        </div>

                        <div 
                            className="ImportDocument__arrow" 
                            onClick={handleArrowClick}
                            title="Click to continue"
                            aria-label="Continue to next step"
                        >
                            ❯
                        </div>
                    </>
                );

            case 'upload':
                return (
                    <UploadZipFile 
                        onNext={handleZipUploaded}
                        onBack={handleBackToIntro}
                    />
                );

            case 'configure':
                return (
                    <ConfigureWorkspaceSpaceImport 
                        extractedData={extractedData}
                        onNext={handleConfigurationComplete}
                        onBack={handleBackToUpload}
                        workspaceDetails={workspaceDetails}
                    />
                );

            case 'process':
                return (
                    <ProcessWorkspaceSpaceImport
                        spaceConfig={spaceConfig}
                        extractedData={extractedData}
                        onComplete={handleProcessComplete}
                        onBack={handleBackToConfigure}
                        workspaceId={workspaceDetails?.id}
                    />
                );

            case 'completing':
                return (
                    <div className="ImportDocument__main-div">
                        <div className="ImportDocument__content">
                            <p>Redirecting to your workspace...</p>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    if (!uid) {
        return (
            <div className="DocumentationHomeComponent__loading-animation-div">
                <i className="fas fa-spinner fa-spin DocumentationHomeComponent__loading-animation-icon"></i>
            </div>
        );
    }

    return (
        <div 
            className={`ImportDocument__main-div ${currentStep === 'intro' && isTransitioning ? 'transitioning' : isTransitioning ? 'transitioning-back' : ''}`}
            onContextMenu={
                enhancedRightClickEnabled
                ? (e) => {
                    e.preventDefault();
                    showContextMenu(e.clientX, e.clientY);
                    }
                : undefined
            }
        >
            {renderStep()}
            
            {/* Back arrow - only show on intro step when onBack is provided */}
            {currentStep === 'intro' && onBack && (
                <div 
                    className="ImportDocument__arrow ImportDocument__arrow--back" 
                    onClick={handleBackClick}
                    title="Go back"
                    aria-label="Go back to previous step"
                >
                    ❮
                </div>
            )}
            {enhancedRightClickEnabled && ContextMenuComponent}
        </div>
    );
}

export default ImportWorkspaceSpace;
